import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getAccountPlanBadgeClass, getAccountPlanLabel } from '../utils/accountPlanLabel';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

/**
 * Reusable account search and select component
 * 
 * @param {Object} props
 * @param {Object} props.selectedAccount - Currently selected account object
 * @param {Function} props.onSelect - Callback when an account is selected: (account) => void
 * @param {Function} props.onClear - Callback when selection is cleared: () => void
 * @param {string} props.placeholder - Input placeholder text
 * @param {string} props.label - Label text for the input
 * @param {boolean} props.required - Whether selection is required
 * @param {string} props.error - Error message to display
 * @param {string} props.searchEndpoint - Custom search endpoint (default: /api/admin/accounts/search)
 */
const AccountSearchSelect = ({
  selectedAccount,
  onSelect,
  onClear,
  placeholder = 'Search accounts...',
  label = 'Account',
  required = false,
  error = null,
  searchEndpoint = '/api/admin/accounts/search'
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search accounts API call
  const searchAccounts = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSearchResults([]);
      return;
    }
    
    try {
      setIsSearching(true);
      const response = await axios.get(`${API_BASE_URL}${searchEndpoint}`, {
        params: { q: query, limit: 10 }
      });
      setSearchResults(response.data.data || []);
    } catch (error) {
      console.error('Error searching accounts:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchEndpoint]);

  // Handle search input change with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setShowDropdown(true);
    
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    searchTimeoutRef.current = setTimeout(() => {
      searchAccounts(value);
    }, 300);
  };

  // Handle account selection
  const handleSelect = (account) => {
    setSearchQuery(account.name);
    setShowDropdown(false);
    setSearchResults([]);
    onSelect(account);
  };

  // Handle clear selection
  const handleClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    onClear();
    searchInputRef.current?.focus();
  };

  // Tier badge colors
  return (
    <div className="relative" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      {selectedAccount ? (
        // Selected account display
        <div className="flex items-center justify-between p-3 border border-gray-300 rounded-lg bg-gray-50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
              <span className="text-primary-700 font-semibold">
                {selectedAccount.name?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <p className="font-medium text-gray-900">{selectedAccount.name}</p>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getAccountPlanBadgeClass(selectedAccount)}`}>
                  {getAccountPlanLabel(selectedAccount)}
                </span>
                {selectedAccount.primaryEmail && (
                  <span>{selectedAccount.primaryEmail}</span>
                )}
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-200"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      ) : (
        // Search input
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isSearching ? (
              <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full"></div>
            ) : (
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onFocus={() => searchQuery && setShowDropdown(true)}
            placeholder={placeholder}
            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
              error ? 'border-red-500' : 'border-gray-300'
            }`}
          />
        </div>
      )}
      
      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
      
      {/* Search results dropdown */}
      {showDropdown && searchResults.length > 0 && !selectedAccount && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
          {searchResults.map((account) => (
            <button
              key={account.id}
              type="button"
              onClick={() => handleSelect(account)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0"
            >
              <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                <span className="text-gray-600 font-medium text-sm">
                  {account.name?.charAt(0)?.toUpperCase() || '?'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{account.name}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getAccountPlanBadgeClass(account)}`}>
                    {getAccountPlanLabel(account)}
                  </span>
                  {account.primaryEmail && (
                    <span className="truncate">{account.primaryEmail}</span>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
      
      {/* No results message */}
      {showDropdown && searchQuery && searchResults.length === 0 && !isSearching && !selectedAccount && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
          No accounts found for "{searchQuery}"
        </div>
      )}
    </div>
  );
};

export default AccountSearchSelect;
